'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { Heart, ArrowLeft, Search } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { HotelCard } from './HotelCard';
import { useWishlist } from '../hooks/useWishlist';
import { usePublicHotels } from '../hooks/useHotels';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  SavedHotels                                                                */
/* -------------------------------------------------------------------------- */

export function SavedHotels() {
  const { t } = useTranslation();
  const { savedIds, isSaved, toggleSave } = useWishlist();
  const { data: allHotels } = usePublicHotels();

  const savedHotels = useMemo(
    () => (allHotels ?? []).filter((h) => savedIds.includes(h.id)),
    [savedIds, allHotels],
  );

  return (
    <div id="hotels-saved-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link id="hotels-saved-back" href="/hotels" className="inline-flex items-center gap-1.5 text-xs text-hms-textSub hover:text-hms-gold transition-colors mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> {t('hotelSearch.backToHotels')}
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-hms-red/10 border border-hms-red/20 flex items-center justify-center">
            <Heart className="w-5 h-5 text-hms-red fill-hms-red" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-hms-text">{t('hotelSearch.savedHotels')}</h2>
            <p className="text-xs text-hms-textSub mt-0.5">
              {savedHotels.length > 0
                ? savedHotels.length === 1
                  ? t('hotelSearch.savedCount', { count: '1' })
                  : t('hotelSearch.savedCountPlural', { count: String(savedHotels.length) })
                : t('hotelSearch.noSavedHotelsYet')}
            </p>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {savedHotels.length === 0 && (
        <div id="hotels-saved-empty" className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-2xl bg-hms-surface border border-hms-border flex items-center justify-center mb-6">
            <Heart className="w-10 h-10 text-hms-textDim" />
          </div>
          <h3 className="text-lg font-bold text-hms-text mb-2">{t('hotelSearch.noSavedHotelsTitle')}</h3>
          <p className="text-sm text-hms-textSub max-w-sm mb-6">
            {t('hotelSearch.noSavedHotelsDesc')}
          </p>
          <Link id="hotels-saved-browse" href="/hotels"
            className={cn(
              'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
              'bg-hms-gold text-black text-xs font-bold',
              'hover:brightness-110 transition-all shadow-lg shadow-hms-gold/20',
            )}>
            <Search className="w-3.5 h-3.5" /> {t('hotelSearch.browseHotels')}
          </Link>
        </div>
      )}

      {/* Grid of saved hotels */}
      {savedHotels.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {savedHotels.map((hotel) => (
            <HotelCard
              key={hotel.id}
              hotel={hotel}
              dateQuery=""
              isCompareSelected={false}
              onToggleCompare={() => {}}
              compareDisabled
              isSaved={isSaved(hotel.id)}
              onToggleSave={toggleSave}
            />
          ))}
        </div>
      )}
    </div>
  );
}
